'use client';

import { CommandDialog, CommandInput } from '@/components/ui/command';
import { useCommandMenu } from '@/contexts/command-menu-context';
import {
  useRegisteredCommands,
  useRegisteredOpenTabs,
} from '@/contexts/command-registry-context';
import { boostByFrecency, useFrecency } from '@/hooks/use-frecency';
import { api, useQuery } from '@/lib/convex';
import { useConvexAuth } from 'convex/react';
import { useParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { MenuFooter } from './menu-footer';
import { Kbd, formatRelativeTime } from './primitives';
import { useEntitySearch } from './hooks/use-entity-search';
import { filterCommands, useQuickActions } from './hooks/use-quick-actions';
import { useMenuStack } from './hooks/use-menu-stack';
import { pageForPrefix, type MenuPage } from './types';
import { searchWorkspaceOpenTabs } from '@/lib/workspace-open-tab-search';
import { getCommandOpenTabsCopy } from '@/i18n/command-open-tabs-copy';
import { useGlobalCommandMenuCopy } from './global-command-menu-copy';
import { GlobalCommandMenuContent } from './global-command-menu-content';
import { useGlobalCommandMenuActions } from './hooks/use-global-command-menu-actions';

export function GlobalCommandMenu() {
  const t = useTranslations('CommandMenu');
  const openTabsCopy = getCommandOpenTabsCopy(useLocale());
  const params = useParams();
  const orgSlug = (params?.orgSlug as string) || undefined;
  const { isAuthenticated } = useConvexAuth();
  const { isOpen, close, query, initialPage } = useCommandMenu();

  const text = useGlobalCommandMenuCopy();

  const [input, setInput] = useState('');
  const { page, stack, push, pop, reset } = useMenuStack();

  useEffect(() => {
    if (isOpen) {
      startTransition(() => {
        setInput(query);
        reset(initialPage ? [initialPage] : []);
      });
    }
  }, [isOpen, query, initialPage, reset]);

  const pushPage = useCallback(
    (next: MenuPage) => {
      startTransition(() => {
        push(next);
        setInput('');
      });
    },
    [push]
  );

  const popPage = useCallback(() => {
    startTransition(() => {
      pop();
      setInput('');
    });
  }, [pop]);

  const handleValueChange = useCallback(
    (value: string) => {
      if (page.kind === 'root') {
        const prefixPage = pageForPrefix(value.trimStart().charAt(0));
        if (prefixPage) {
          // Pasted "#foo" keeps "foo" as the sub-page search term.
          const remainder = value.trimStart().slice(1);
          startTransition(() => {
            push(prefixPage);
            setInput(remainder);
          });
          return;
        }
      }
      setInput(value);
    },
    [page.kind, push]
  );

  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        event.key === 'Backspace' &&
        input === '' &&
        stack.length > 0 &&
        !event.nativeEvent.isComposing
      ) {
        event.preventDefault();
        popPage();
      }
    },
    [input, stack.length, popPage]
  );

  const {
    products: rawProducts,
    threads: rawThreads,
    orgs,
    projects,
    avatars,
    aesthetics,
    members,
    organizationSlugById,
    shouldSearch,
    term,
  } = useEntitySearch({ page, input, orgSlug, isAuthenticated });

  const { data: viewer } = useQuery(api.users.queries.viewer);
  const { record, score, topEntries } = useFrecency({
    userId: viewer?._id ? String(viewer._id) : undefined,
    orgSlug,
  });

  const products = useMemo(
    () =>
      boostByFrecency(rawProducts, (p) => `product:${String(p._id)}`, score),
    [rawProducts, score]
  );
  const threads = useMemo(
    () => boostByFrecency(rawThreads, (t) => `thread:${t.routeRef}`, score),
    [rawThreads, score]
  );

  const recentEntries = useMemo(() => {
    if (page.kind !== 'root' || term) return [];
    return topEntries(5, (entry) => Boolean(entry.meta?.href));
  }, [page.kind, term, topEntries]);

  const quickActions = useQuickActions({
    orgSlug,
    isAuthenticated,
    text,
    close,
  });

  const registeredCommands = useRegisteredCommands();
  const registeredOpenTabs = useRegisteredOpenTabs();
  const openTabResults = useMemo(
    () =>
      page.kind === 'root'
        ? searchWorkspaceOpenTabs(
            registeredOpenTabs,
            term,
            term.length > 0 ? 12 : 6
          )
        : [],
    [page.kind, registeredOpenTabs, term]
  );
  const openTabTextLoading =
    page.kind === 'root' && registeredOpenTabs.some((tab) => tab.textLoading);
  const themeCommand = useMemo(
    () => quickActions.find((command) => command.id === 'toggle-theme'),
    [quickActions]
  );

  const filteredActions = useMemo(() => {
    if (page.kind === 'scope' || page.kind === 'picker') return [];
    const actions = quickActions.filter(
      (command) => command.id !== 'toggle-theme'
    );
    if (page.kind === 'root' && !term) return actions;
    return filterCommands(actions, term);
  }, [page.kind, quickActions, term]);

  const filteredRegistered = useMemo(() => {
    if (page.kind === 'scope' || page.kind === 'picker') return [];
    return filterCommands(registeredCommands, term);
  }, [page.kind, registeredCommands, term]);

  const filteredPageActions = useMemo(() => {
    if (page.kind === 'scope' || page.kind === 'picker') return [];
    if (!themeCommand) return filteredRegistered;
    if (term && filterCommands([themeCommand], term).length === 0) {
      return filteredRegistered;
    }
    return [
      themeCommand,
      ...filteredRegistered.filter((command) => command.id !== themeCommand.id),
    ];
  }, [filteredRegistered, page.kind, term, themeCommand]);

  // If the page that registered an open picker unmounts (navigation), the
  // picker's callbacks are stale — fall back to the root page.
  useEffect(() => {
    if (page.kind !== 'picker') return;
    if (registeredCommands.some((command) => command.id === page.commandId)) {
      return;
    }
    startTransition(() => {
      reset();
      setInput('');
    });
  }, [page, registeredCommands, reset]);

  const formatTime = useCallback(
    (timestamp: number) => formatRelativeTime(timestamp, t),
    [t]
  );

  const roleLabels = useMemo(
    () => ({
      owner: t('roleOwner'),
      admin: t('roleAdmin'),
      member: t('roleMember'),
    }),
    [t]
  );

  const {
    gotoItems,
    onNavigate,
    onPushScope,
    onRunCommand,
    onSelectAesthetic,
    onSelectAvatar,
    onSelectMember,
    onSelectOpenTab,
    onSelectOrg,
    onSelectProduct,
    onSelectProject,
    onSelectRecent,
    onSelectThread,
  } = useGlobalCommandMenuActions({
    page,
    term,
    text,
    orgSlug,
    organizationSlugById,
    close,
    pushPage,
    record,
  });

  const onPrefix = useCallback(
    (prefix: string) => {
      const prefixPage = pageForPrefix(prefix);
      if (prefixPage) pushPage(prefixPage);
    },
    [pushPage]
  );

  const placeholder = useMemo(() => {
    switch (page.kind) {
      case 'scope':
        return page.scope === 'products'
          ? text.searchProducts
          : page.scope === 'threads'
            ? text.searchThreads
            : text.searchAll;
      case 'actions':
        return text.runCommand;
      case 'picker':
        return page.subPage.placeholder ?? text.searchAll;
      case 'root':
        return text.searchAll;
    }
  }, [page, text]);

  const breadcrumbLabel = useMemo(() => {
    switch (page.kind) {
      case 'scope':
        switch (page.scope) {
          case 'products':
            return text.products;
          case 'threads':
            return text.threads;
          case 'projects':
            return text.projects;
          case 'avatars':
            return text.avatars;
          case 'aesthetics':
            return text.aesthetics;
          case 'members':
            return text.members;
        }
      case 'actions':
        return text.commands;
      case 'picker':
        return page.label;
      case 'root':
        return null;
    }
  }, [page, text]);

  const visibleResults =
    page.kind === 'picker'
      ? 1 // picker pages own their content and empty states
      : products.length +
        threads.length +
        orgs.length +
        projects.length +
        avatars.length +
        aesthetics.length +
        members.length +
        gotoItems.length +
        filteredActions.length +
        filteredPageActions.length +
        openTabResults.length;

  const waitingForTypedScope =
    page.kind === 'scope' &&
    !shouldSearch &&
    (page.scope === 'avatars' || page.scope === 'aesthetics');

  const showEmptyState =
    visibleResults === 0 &&
    !openTabTextLoading &&
    !waitingForTypedScope &&
    (shouldSearch || page.kind !== 'root');

  return (
    <CommandDialog
      open={isOpen}
      onOpenChange={(v) => (!v ? close() : null)}
      className="top-[20%] !z-[10003] translate-y-0 sm:max-w-[640px]"
      overlayClassName="!z-[10003]"
      commandProps={{ shouldFilter: false }}
    >
      <CommandInput
        value={input}
        onValueChange={handleValueChange}
        onKeyDown={handleInputKeyDown}
        placeholder={placeholder}
        trailing={<Kbd className="hidden shrink-0 sm:inline-flex">esc</Kbd>}
      />
      <GlobalCommandMenuContent
        page={page}
        stackDepth={stack.length}
        text={text}
        breadcrumbLabel={breadcrumbLabel}
        showEmptyState={showEmptyState}
        openTabs={{
          results: openTabResults,
          copy: openTabsCopy,
          onSelect: onSelectOpenTab,
        }}
        pageActions={{
          commands: filteredPageActions,
          heading: text.pageActions,
          onRun: onRunCommand,
        }}
        rootPage={{
          text,
          shouldSearch,
          recentEntries,
          onSelectRecent,
          products,
          threads,
          orgs,
          projects,
          avatars,
          aesthetics,
          members,
          gotoItems,
          canSearchWorkspaceScopes: Boolean(orgSlug),
          roleLabels,
          quickActions: filteredActions,
          formatTime,
          onNavigate,
          onSelectProduct,
          onSelectThread,
          onSelectOrg,
          onSelectProject,
          onSelectAvatar,
          onSelectAesthetic,
          onSelectMember,
          onPushScope,
          onRunCommand,
        }}
        scopePage={{
          scope: page.kind === 'scope' ? page.scope : 'products',
          text,
          shouldSearch,
          products,
          threads,
          projects,
          avatars,
          aesthetics,
          members,
          roleLabels,
          formatTime,
          onSelectProduct,
          onSelectThread,
          onSelectProject,
          onSelectAvatar,
          onSelectAesthetic,
          onSelectMember,
        }}
        actionsPage={{
          heading: text.commands,
          actions: filteredActions,
          onRunCommand,
        }}
        term={term}
        orgSlug={orgSlug}
        close={close}
        popPage={popPage}
      />
      <MenuFooter
        text={text}
        atRoot={stack.length === 0}
        onPrefix={onPrefix}
        onBack={popPage}
      />
    </CommandDialog>
  );
}
